import {
  CandidateResitRegistrationSchema,
  GroupResitSchema,
  GroupResitWithAccessLinkRequest,
  ResitCoordinatorAccess,
  ResitSimplePaymentDetail,
} from '../../../../../models/@candidate/candidateResitRegistrationSchema';
import { axiosExternalRecourseFactory, axiosFactory } from '../../../../client';
import { KeyValueResponse } from '../../../../model';
import { CoordinatorSubjectSummaryRequest, GetSubjectSummaryResponse } from '../../model';
import {
  CoordinatorResitUploadParam,
  GetSetUploadedStatusRequest,
  PreSignUploadURLResponse,
  ResitConfigurationResponse,
  ResitDownloadRequest,
  ResitDownloadResponse,
  ResitGroupApplicationStatus,
  ResitRegisterFileScanningStatusResponse,
  ResitRegisterTemplatePreSignUrlResponse,
  ResitSummaryDownloadRequest,
  ResitSummaryDownloadResponse,
} from '../model';
import { ResitCandidateSearchRequest, WindowPeriodByExamLevelResponse } from './models';
import * as ROUTES from './routes';

const baseURL = `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/resit`;
const timeout = 5000;

const resitClient = axiosFactory(baseURL, timeout);
const resitUserCoordinatorClient = axiosFactory(`/user/api/`, timeout);
const resitCoordinatorClient = axiosFactory(
  `${import.meta.env.VITE_APP_API_DOMAIN}/candidate/api/`,
  timeout
);
export const uploadClient = axiosFactory(``, timeout);
export const externalUploadClient = axiosExternalRecourseFactory(``, timeout);

/** to get coordinator registration option
 */
export const getResitRegistrationOptions = () =>
  resitUserCoordinatorClient.get<KeyValueResponse[]>(`${ROUTES.GET_RESIT_REGISTER_OPTIONS}`);

export const postResitCandidateSearch = (csrfToken: string, data: ResitCandidateSearchRequest) =>
  resitClient.post<CandidateResitRegistrationSchema>(csrfToken, ROUTES.POST_CANDIDATE_SEARCH, data);

/** to retrieve open time window for particular exam level
 * */
export const getWindowPeriodByExamLevel = (examLevel: string) =>
  resitCoordinatorClient.get<WindowPeriodByExamLevelResponse>(
    `${ROUTES.GET_WINDOW_PERIOD}/${examLevel}`
  );

/** get download url to download the csv
 * */
export const getCSVTemplateDownloadUrl = () =>
  resitClient.get<ResitRegisterTemplatePreSignUrlResponse>(ROUTES.GET_TEMPLATE_DOWNLOAD_URL);

/** get pre-sign url to upload the csv
 * */
export const getPreSignUploadUrl = () =>
  resitClient.get<PreSignUploadURLResponse>(`${ROUTES.GET_PRE_SIGN_UPLOAD_URL}/.csv`);

/** to validate upload file
 * */
export const postValidateUploadFile = (csrfToken: string, params: CoordinatorResitUploadParam) =>
  resitClient.post<CandidateResitRegistrationSchema[]>(
    csrfToken,
    `${ROUTES.POST_VALIDATE_UPLOAD_FILE}/${params.key}/${params.examLevel}`,
    {},
    {
      timeout: 30000,
    }
  );

/** to post upload status
 * */
export const postSetUploadStatusUploaded = (csrfToken: string, data: GetSetUploadedStatusRequest) =>
  uploadClient.post<any>(
    csrfToken,
    `${ROUTES.POST_SET_FILE_STATUS_UPLOADED}${data.key}/${data.fileName}`
  );

/** get upload file scan status
 * */
export const getUploadFileScanStatus = (key: string) =>
  uploadClient.get<ResitRegisterFileScanningStatusResponse>(
    `${ROUTES.GET_UPLOAD_FILE_SCAN_STATUS}/${key}`
  );

/** to upload file to s3
 * */
export const putUploadFileToS3 = (data: File | FormData, putUrl: string) =>
  externalUploadClient.put<any>(putUrl, data);

/** get subject summary
 * */
export const postSubjectSummary = (csrfToken: string, payload: CoordinatorSubjectSummaryRequest) =>
  resitClient.post<GetSubjectSummaryResponse>(csrfToken, ROUTES.POST_SUBJECT_SUMMARY, payload);

/** get group subjects fee & gst */
export const postGroupFee = (csrfToken: string, payload: GroupResitSchema) =>
  resitClient.post<GroupResitSchema>(csrfToken, ROUTES.POST_RESIT_GROUP_FEE, payload);

/** save group subjects fee & gst for list of candidates, send group email to candidate by coordinator */
export const postCandidateGroupFeeSaving = (
  csrfToken: string,
  payload: GroupResitWithAccessLinkRequest
) => resitClient.post<undefined>(csrfToken, ROUTES.POST_RESIT_CANDIDATE_GROUP_FEE_SAVING, payload,{timeout:30000,});

/** get simplified resit payment detail (after success) */
export const getSimplifiedPaymentDetail = (internalTransactionRef: string) =>
  resitClient.get<ResitSimplePaymentDetail>(
    ROUTES.GET_RESIT_SIMPLIFIED_PAYMENT_DETAIL(internalTransactionRef)
  );

/** To download coordinator resit summary */
export const postCoordinatorDownloadSummary = (
  csrfToken: string,
  data: ResitSummaryDownloadRequest
) =>
  resitClient.post<ResitSummaryDownloadResponse>(
    csrfToken,
    ROUTES.POST_RESIT_COORDINATOR_DOWNLOAD_SUMMARY,
    data
  );

/** get coordinator resit acess, to send email or make payment */
export const getCoordinatorResitAccess = (examCode: string) =>
  resitClient.get<ResitCoordinatorAccess>(ROUTES.GET_RESIT_COORDINATOR_ACCESS(examCode));

/** get application detail, same as receipt summary, but for coordinator resit bulk send email */
export const getResitApplicationStatus = (id: string) =>
  resitClient.get<ResitGroupApplicationStatus>(ROUTES.GET_RESIT_APPLICATION_STATUS(id));

/** get coordinator resit application (with email) download summary s3 pre-signed url */
export const getCoordinatorApplicationDownloadSummary = (
  csrfToken: string,
  data: ResitDownloadRequest
) =>
  resitClient.post<ResitDownloadResponse>(
    csrfToken,
    ROUTES.GET_RESIT_APPLICATION_DOWNLOAD_SUMMARY,
    data
  );

/** get resit configuration for example number of resit candidate at one time */
export const getResitConfiguration = () =>
  resitClient.get<ResitConfigurationResponse>(ROUTES.GET_RESIT_CONFIGURATION);
